import { CardContent, Divider, Tooltip, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LegendItem, Polygon as PolygonStyle, Raster } from "helpers/models";
import {
  PolygonLayerStatistic,
  PolygonMaxValuePoint,
  PolygonMetricValues
} from "./types";
import styles from "./polygon-result.module.scss";

interface Props {
  item: PolygonLayerStatistic;
  legendInfo?: LegendItem;
  metrics?: PolygonMetricValues;
  intensityMetrics?: PolygonMetricValues;
  maxValuePoints: PolygonMaxValuePoint[];
  maxValuePoint?: PolygonMaxValuePoint;
  formatPercent: (value?: number) => string;
  formatCoordinatePair: (longitude?: number, latitude?: number) => string;
  onCoordinateClick: () => void;
}

const meanFromIntensityLayerIds = new Set([
  "FL_Fluvial_Official",
  "FL_Fluvial_SwissRe",
  "FL_Surge_SwissRe",
  "FL_Pluvial_SwissRe"
]);

const transparentBorderRiskLevels = new Set([
  "Negligible",
  "Negligible (< 0.014)"
]);

const coordinateHiddenRiskLevels = new Set([
  "Negligible",
  "Outside",
  "No Observation",
  "Waterbody",
  "No Data",
  "No_Data"
]);

const normalizeRiskLevelForLegend = (riskLevel: string, layerId: string) => {
  let sortIndex: number | undefined;
  let normalizedRiskLevel = riskLevel;

  if (layerId.includes("Fathom")) {
    if (["5 years", "10 years", "20 years", "50 years"].includes(riskLevel)) {
      if (riskLevel === "5 years") {
        sortIndex = 9;
      } else if (riskLevel === "10 years") {
        sortIndex = 8;
      } else if (riskLevel === "20 years") {
        sortIndex = 7;
      }
      normalizedRiskLevel = "≤50 years";
    } else if (riskLevel === "1000 years") {
      normalizedRiskLevel = "≥1000 years";
    }
  } else if (layerId === "FL_Fluvial_Official" && riskLevel === "> 500 years") {
    normalizedRiskLevel = "> 500 years (US)";
  } else if (
    riskLevel === "Negligible (< 0.014)" ||
    riskLevel === "Negligible"
  ) {
    sortIndex = 0.5;
  }

  return {
    normalizedRiskLevel,
    sortIndex
  };
};

const getLegendColor = (
  legendInfo: LegendItem | undefined,
  riskLevel: string
) => {
  const normalizedRiskLevel = String(riskLevel || "").trim();

  if (
    normalizedRiskLevel === "Outside" ||
    normalizedRiskLevel === "No Observation"
  ) {
    return {
      color: "#f2f2f2",
      opacity: 0.5
    };
  }

  if (
    normalizedRiskLevel === "" ||
    normalizedRiskLevel === "No Data" ||
    normalizedRiskLevel === "No_Data"
  ) {
    return {
      color: "grey",
      opacity: 0.5
    };
  }

  if (normalizedRiskLevel === "Waterbody") {
    return {
      color: "#DCDBDC",
      opacity: 0.5
    };
  }

  const layerId = String(legendInfo?.layerId || "");
  const normalized = normalizeRiskLevelForLegend(normalizedRiskLevel, layerId);
  const rules = legendInfo?.rules || [];
  const matchedRule =
    rules.find((rule: any) => rule?.title === normalized.normalizedRiskLevel) ||
    rules.find((rule: any) => rule?.title === normalizedRiskLevel);

  const symbol = matchedRule?.symbolizers?.[0];
  const opacity =
    (symbol as Raster | undefined)?.raster?.opacity ??
    (symbol as PolygonStyle | undefined)?.polygon?.fillOpacity ??
    1;

  const shouldKeepBorderVisible =
    layerId === "EQ_Landslide_Global_SwissRe" &&
    transparentBorderRiskLevels.has(normalizedRiskLevel) &&
    opacity === 0;

  return {
    color:
      (symbol as Raster | undefined)?.raster?.color ??
      (symbol as PolygonStyle | undefined)?.polygon?.fill ??
      "rgb(242, 252, 242)",
    opacity,
    shouldKeepBorderVisible
  };
};

const getRiskSortIndex = (
  riskLevel: string,
  layerId: string,
  levelOrder: Map<string, number>
) => {
  const normalizedRiskLevel = String(riskLevel || "").trim();

  if (
    normalizedRiskLevel === "" ||
    normalizedRiskLevel === "No Data" ||
    normalizedRiskLevel === "No_Data" ||
    normalizedRiskLevel === "Outside" ||
    normalizedRiskLevel === "No Observation"
  ) {
    return -1;
  }

  const normalized = normalizeRiskLevelForLegend(normalizedRiskLevel, layerId);
  return (
    normalized.sortIndex ??
    levelOrder.get(normalized.normalizedRiskLevel) ??
    levelOrder.get(normalizedRiskLevel) ??
    0
  );
};

const sortRiskDistributionByLegend = (
  riskDistribution: PolygonLayerStatistic["riskDistribution"] | undefined,
  legendInfo: LegendItem | undefined
) => {
  const list = riskDistribution || [];
  if (!list.length) {
    return list;
  }

  const rules = legendInfo?.rules || [];
  if (!rules.length) {
    return list;
  }

  const levelOrder = new Map<string, number>();
  const layerId = String(legendInfo?.layerId || "");
  rules.forEach((rule: any, index: number) => {
    const title = String(rule?.title || "").trim();
    if (title) {
      levelOrder.set(title, index);
    }
  });

  return [...list].sort((a, b) => {
    const aSortIndex = getRiskSortIndex(
      String(a?.riskLevel || ""),
      layerId,
      levelOrder
    );
    const bSortIndex = getRiskSortIndex(
      String(b?.riskLevel || ""),
      layerId,
      levelOrder
    );
    return bSortIndex - aSortIndex;
  });
};

const formatMetricNumber = (value: unknown) => {
  let numericValue: number | undefined;

  if (typeof value === "number" && Number.isFinite(value)) {
    numericValue = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        numericValue = parsed;
      }
    }
  }

  if (numericValue === undefined) {
    return undefined;
  }

  return String(parseFloat(numericValue.toFixed(2)));
};

function PolygonDetailContent({
  item,
  legendInfo,
  metrics,
  intensityMetrics,
  maxValuePoints,
  maxValuePoint,
  formatPercent,
  formatCoordinatePair,
  onCoordinateClick
}: Props) {
  const [t] = useTranslation();
  const getMetricDisplayValue = (value: unknown) => {
    const formatted = formatMetricNumber(value);
    if (formatted !== undefined) {
      return formatted;
    }
    return t(`legend.${value}`);
  };

  const sortedRiskDistribution = sortRiskDistributionByLegend(
    item.riskDistribution,
    legendInfo
  );
  const shouldHideCoordinateRow =
    sortedRiskDistribution.length === 1 &&
    coordinateHiddenRiskLevels.has(
      String(sortedRiskDistribution[0]?.riskLevel || "").trim()
    );
  const meanMetricValue = meanFromIntensityLayerIds.has(item.layerId)
    ? intensityMetrics?.mean
    : metrics?.mean;

  return (
    <CardContent>
      <div className={styles.riskTitle}>
        {item.layerId.includes("Fathom")
          ? t("drawShape.Values and Coordinates")
          : t("drawShape.Extreme Values and Location Coordinates")}
      </div>
      <div className={styles.riskRow}>
        <span className={styles.riskRowKey}>
          {t(`drawShape.polygonResultMax.${item.layerId}`)}
        </span>
        <span className={styles.riskRowValue}>
          {getMetricDisplayValue(metrics?.max)}
        </span>
      </div>
      {!shouldHideCoordinateRow && (
        <div className={styles.riskRow}>
          <span className={styles.riskRowKey}>
            {t("drawShape.Coordinates")}
          </span>
          <span
            className={styles.riskRowCoordinate}
            onClick={onCoordinateClick}
          >
            <span>
              {formatCoordinatePair(
                maxValuePoint?.longitude,
                maxValuePoint?.latitude
              )}
            </span>
            {maxValuePoints.length > 1 && (
              <Tooltip title={t("drawShape.Multiple Locations")}>
                <IconButton>
                  <span
                    className="material-icons-outlined"
                    style={{
                      fontSize: "18px",
                      color: "rgb(255,160,47)"
                    }}
                  >
                    report_problem
                  </span>
                </IconButton>
              </Tooltip>
            )}
          </span>
        </div>
      )}
      <Divider variant="middle" component="div" style={{ margin: "10px 0" }} />
      <div className={styles.riskTitle}>
        {t(`drawShape.Statistical Summary`)}
      </div>
      <div className={styles.riskRow}>
        <span className={styles.riskRowKey}>
          {t(`drawShape.polygonResultP90.${item.layerId}`)}
        </span>
        <span className={styles.riskRowValue}>
          {getMetricDisplayValue(metrics?.p90)}
        </span>
      </div>
      <div className={styles.riskRow}>
        <span className={styles.riskRowKey}>
          {t(`drawShape.polygonResultMedian.${item.layerId}`)}
        </span>
        <span className={styles.riskRowValue}>
          {getMetricDisplayValue(metrics?.median)}
        </span>
      </div>
      {item.layerId !== "EQ_Landslide_Global_SwissRe" && (
        <div className={styles.riskRow}>
          <span className={styles.riskRowKey}>
            {t(`drawShape.polygonResultMean.${item.layerId}`)}
          </span>
          <span className={styles.riskRowValue}>
            {getMetricDisplayValue(meanMetricValue)}
          </span>
        </div>
      )}
      <Divider variant="middle" component="div" style={{ margin: "10px 0" }} />
      <div className={styles.riskTitle}>{t("drawShape.Area Distribution")}</div>
      {sortedRiskDistribution.map((riskItem) => {
        const swatchStyle = getLegendColor(legendInfo, riskItem.riskLevel);
        const swatchOpacity = swatchStyle.shouldKeepBorderVisible
          ? 1
          : swatchStyle.opacity;
        const swatchBackgroundColor = swatchStyle.shouldKeepBorderVisible
          ? "transparent"
          : swatchStyle.color;

        return (
          <div
            key={`${item.layerId}-${riskItem.riskLevel}`}
            className={styles.riskRowWrap}
          >
            <div className={styles.riskRow}>
              <div className={styles.riskRowKey}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: swatchBackgroundColor,
                    opacity: swatchOpacity,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    marginRight: 5
                  }}
                />
                <span>{t(`legend.${riskItem.riskLevel}`)}</span>
              </div>
              <span className={styles.riskRowValue}>
                {formatPercent(riskItem.ratio)}
              </span>
            </div>
          </div>
        );
      })}
    </CardContent>
  );
}

export default PolygonDetailContent;
